// Utility functions for input formatting and validation in WHS QR Check-In Wizard

/**
 * Format Australian Business Number (ABN) as XX XXX XXX XXX (11 digits max)
 */
export const formatABN = (value) => {
  if (!value) return "";
  const digits = value.replace(/\D/g, "").slice(0, 11);
  if (digits.length <= 2) return digits;
  if (digits.length <= 5) return `${digits.slice(0, 2)} ${digits.slice(2)}`;
  if (digits.length <= 8) return `${digits.slice(0, 2)} ${digits.slice(2, 5)} ${digits.slice(5)}`;
  return `${digits.slice(0, 2)} ${digits.slice(2, 5)} ${digits.slice(5, 8)} ${digits.slice(8, 11)}`;
};

/**
 * Validate Australian Business Number (ABN) length & weighted algorithm
 */
export const isValidABN = (abnStr) => {
  if (!abnStr) return false;
  const digits = abnStr.replace(/\D/g, "");
  if (digits.length !== 11) return false;

  const weights = [10, 1, 3, 5, 7, 9, 11, 13, 15, 17, 19];
  const firstDigitSubtracted = parseInt(digits[0], 10) - 1;
  const sum = weights.reduce((acc, weight, idx) => {
    const d = idx === 0 ? firstDigitSubtracted : parseInt(digits[idx], 10);
    return acc + d * weight;
  }, 0);

  return sum % 89 === 0;
};

/**
 * Format Australian / General Phone Number
 * e.g. 0412 345 678
 */
export const formatPhoneNumber = (value) => {
  if (!value) return "";
  const cleaned = value.replace(/[^\d+]/g, "");
  if (cleaned.startsWith("+")) {
    const numOnly = cleaned.slice(1);
    if (numOnly.length <= 2) return `+${numOnly}`;
    if (numOnly.length <= 5) return `+${numOnly.slice(0, 2)} ${numOnly.slice(2)}`;
    if (numOnly.length <= 8) return `+${numOnly.slice(0, 2)} ${numOnly.slice(2, 5)} ${numOnly.slice(5)}`;
    return `+${numOnly.slice(0, 2)} ${numOnly.slice(2, 5)} ${numOnly.slice(5, 8)} ${numOnly.slice(8, 13)}`;
  }
  
  const digits = cleaned.replace(/\D/g, "").slice(0, 10);
  if (digits.length <= 4) return digits;
  if (digits.length <= 7) return `${digits.slice(0, 4)} ${digits.slice(4)}`;
  return `${digits.slice(0, 4)} ${digits.slice(4, 7)} ${digits.slice(7, 10)}`;
};

/**
 * Normalize pasted or input phone numbers, particularly handling:
 * - "+61 451 236 532" -> "+61451236532"
 * - "+6161451236532" (duplicate country code) -> "+61451236532"
 * - "+61 0451 236 532" -> "+61451236532"
 * - "0451 236 532" -> "+61451236532"
 * - "451 236 532" -> "+61451236532"
 * - International numbers e.g. "+1 555 123 4567"
 */
export const normalizePhoneInput = (input) => {
  if (!input) return "";
  const str = String(input).trim();
  const hasPlus = str.startsWith("+");
  const digits = str.replace(/\D/g, "");

  if (!digits) return hasPlus ? "+" : "";

  // If input starts with duplicate 6161... (e.g. pasted into a field that already had 61)
  if (digits.startsWith("6161")) {
    let remainder = digits.slice(4);
    if (remainder.startsWith("0")) remainder = remainder.slice(1);
    return `+61${remainder}`;
  }

  // If input starts with 61...
  if (digits.startsWith("61")) {
    let remainder = digits.slice(2);
    // Strip leading 0 if trunk code was included, e.g. +61 04...
    if (remainder.startsWith("0")) remainder = remainder.slice(1);
    return `+61${remainder}`;
  }

  // If input has leading '+' but another country code
  if (hasPlus) {
    return `+${digits}`;
  }

  // Australian local formats:
  // Starts with 0 (e.g. 0451 236 532)
  if (digits.startsWith("0")) {
    return `+61${digits.slice(1)}`;
  }

  // 9-digit mobile starting with 4 (e.g. 451 236 532)
  if (digits.length === 9 && digits.startsWith("4")) {
    return `+61${digits}`;
  }

  // Default fallback for Australian numbers
  return `+61${digits}`;
};

/**
 * Validate Email address format
 */
export const isValidEmail = (email) => {
  if (!email) return false;
  const re = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
  return re.test(String(email).trim().toLowerCase());
};

/**
 * Check if company name matches any variation of Sunny Homes
 * e.g. "Sunny homes", "sunnyhomes", "sunny_homes", "Sunny Homes", "sunny homes pty ltd"
 */
export const isSunnyHomes = (companyName) => {
  if (!companyName) return false;
  const normalized = String(companyName).toLowerCase().replace(/[\s\-_]/g, "");
  return normalized === "sunnyhomes" || normalized.includes("sunnyhome");
};

